/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';

import { PAGE_HEADER_Z_INDEX } from 'bundles/composable-page-header/constants';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import TwoPanelConversationalDiscoveryChatContainer from 'bundles/unified-description-page-common/components/coach/TwoPanelConversationalDiscoveryChatContainer';
import {
  PAGE_CONTENT_CONTAINER_NAME,
  XDP_CHAT_PANEL_WIDTH,
  XDP_GLOBAL_HEADER_HEIGHT,
} from 'bundles/unified-description-page-common/constants';
import { usePageRefs } from 'bundles/unified-description-page-common/contexts/PageRefsContext';
import { useTwoPanelConversationalDiscoveryExperiment } from 'bundles/unified-description-page-common/providers/specialization/TwoPanelConversationalDiscoveryExperimentProvider';

// useLayoutEffect logs a dev warning during SSR (effects don't run on the server). useEffect on
// the server is silent and harmless since it doesn't run there either; on the client we keep
// useLayoutEffect's pre-paint timing so the chat doesn't flash at its fallback `top` on mount.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Scroll threshold to activate fixed nav measurements.
 * Set above zero to account for smart-header's transform-back animation that briefly
 * leaves rect.top negative even after scrollY hits 0. Value chosen to exceed typical
 * header animation bounce-back distance.
 */
const TOP_OF_PAGE_THRESHOLD = 100;

/**
 * Calculates the gutter on the meta-nav side of the content column.
 * Equals half the viewport overflow beyond the lg breakpoint, floored at 0.
 */
export const PAGE_GUTTER = `max(0px, calc((100vw - ${breakpoints.values.lg}px) / 2))`;

/**
 * Calculates the gutter on the chat side of the content column.
 * Equals PAGE_GUTTER minus the chat panel width, floored at 0.
 * Becomes positive on ultra-wide viewports (~lg + 2×chat width).
 */
export const CHAT_SIDE_BLEED = `max(0px, calc(${PAGE_GUTTER} - ${XDP_CHAT_PANEL_WIDTH}px))`;

type Props = React.PropsWithChildren;

const styles = {
  // Outer wrapper that clips the hero's 200vw full-bleed bg at viewport edges.
  panelGridWrapper: css`
    ${breakpoints.up('md')} {
      overflow-x: clip;
    }
  `,
  // Reserves the chat track when visible; single column when dismissed.
  panelGrid: (chatOnLeft: boolean, chatVisible: boolean) => {
    let gridTemplateColumns = '1fr';
    if (chatVisible && chatOnLeft) {
      gridTemplateColumns = `${XDP_CHAT_PANEL_WIDTH}px 1fr`;
    } else if (chatVisible) {
      gridTemplateColumns = `1fr ${XDP_CHAT_PANEL_WIDTH}px`;
    }
    return css`
      ${breakpoints.up('md')} {
        display: grid;
        grid-template-columns: ${gridTemplateColumns};
      }
    `;
  },
  // PAGE_GUTTER + CHAT_SIDE_BLEED padding pins the content area to lg so PageGridContainer
  // (maxWidth="lg") doesn't self-center past ultra-wide viewports. No overflow-x: clip — would
  // block the hero's 200vw full-bleed bg.
  //
  // Exposes for descendant modules:
  //   --xdp-content-gutter-{left,right}: gutter on each side. Inner padding for lg-frame
  //     alignment, or negative margin to extend module bgs out to the viewport/chat edge.
  //   --xdp-content-track-left:  left offset of the content track (chat-width if chat-on-left).
  //   --xdp-content-track-width: width of the content track (100vw - chat-width when visible).
  contentColumn: (chatOnLeft: boolean, chatVisible: boolean) => {
    const chatSide = chatVisible ? CHAT_SIDE_BLEED : PAGE_GUTTER;
    return css`
      ${breakpoints.up('md')} {
        min-width: 0;
        container-type: inline-size;
        container-name: ${PAGE_CONTENT_CONTAINER_NAME};
        padding-left: ${chatOnLeft ? chatSide : PAGE_GUTTER};
        padding-right: ${chatOnLeft ? PAGE_GUTTER : chatSide};

        /* Chat is position:fixed (out-of-flow), so grid auto-placement would land this item in
         column 1. For chat-on-left, column 1 is the chat-reserved track — anchor to column 2. */
        ${chatVisible && chatOnLeft ? 'grid-column-start: 2;' : ''}

        --xdp-content-gutter-left: ${chatOnLeft ? chatSide : PAGE_GUTTER};
        --xdp-content-gutter-right: ${chatOnLeft ? PAGE_GUTTER : chatSide};
        --xdp-content-track-width: ${chatVisible ? `calc(100vw - ${XDP_CHAT_PANEL_WIDTH}px)` : '100vw'};
        ${chatVisible && chatOnLeft ? `--xdp-content-track-left: ${XDP_CHAT_PANEL_WIDTH}px;` : ''}
      }
    `;
  },
  // Masks the global meta nav above an active FixedPageNav. Imperatively shown/sized via ref
  // so per-scroll height changes don't re-render the React tree.
  globalHeaderCover: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: none;
    background: var(--cds-color-neutral-background-primary);
    z-index: ${PAGE_HEADER_Z_INDEX};
    pointer-events: none;
  `,
};

const TwoPanelConversationalDiscoveryLayout = ({ children }: Props) => {
  const { chatOnLeft, chatOnRight } = useTwoPanelConversationalDiscoveryExperiment();
  const { coachOpen } = useCoachState();
  const { pageHeaderRef, fixedPageNavRef } = usePageRefs() ?? {};

  // Chat is fixed full-viewport-height; only `top` updates per scroll (smart-header tracking).
  // Footer overlap is handled at the page level — see ConsumerHeaderFooterWrapper.
  const chatAsideRef = useRef<HTMLElement | null>(null);
  const headerCoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafId = 0;

    const measure = () => {
      const globalHeader = pageHeaderRef?.current;
      const globalHeaderBottom = globalHeader
        ? Math.max(0, globalHeader.getBoundingClientRect().bottom)
        : XDP_GLOBAL_HEADER_HEIGHT;

      let navHeight = 0;
      // Skip near top-of-page; see TOP_OF_PAGE_THRESHOLD.
      if (window.scrollY >= TOP_OF_PAGE_THRESHOLD) {
        const fixedNav = fixedPageNavRef?.current;
        if (fixedNav) {
          const rect = fixedNav.getBoundingClientRect();
          if (rect.height > 0 && rect.bottom > 0) navHeight = rect.bottom;
        }
      }

      const top = navHeight > 0 ? 0 : globalHeaderBottom;

      if (chatAsideRef.current) {
        chatAsideRef.current.style.top = `${top}px`;
      }
      if (headerCoverRef.current) {
        if (navHeight > 0) {
          headerCoverRef.current.style.display = 'block';
          headerCoverRef.current.style.height = `${navHeight}px`;
        } else {
          headerCoverRef.current.style.display = 'none';
        }
      }
    };

    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    // Smart-header uses a 200ms CSS transform transition. rAF-on-scroll captures intermediate
    // values during scroll, but stops firing if the user stops scrolling mid-transition —
    // leaving chat `top` stuck at an intermediate offset. transitionend re-measures on settle.
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'transform' && event.target === pageHeaderRef?.current) {
        schedule();
      }
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('transitionend', onTransitionEnd);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [pageHeaderRef, fixedPageNavRef]);

  // Apply the current header offset on chat mount; scroll listener won't fire until first scroll.
  useIsomorphicLayoutEffect(() => {
    if (!coachOpen || !chatAsideRef.current) return;
    const globalHeader = pageHeaderRef?.current;
    const globalHeaderBottom = globalHeader
      ? Math.max(0, globalHeader.getBoundingClientRect().bottom)
      : XDP_GLOBAL_HEADER_HEIGHT;
    chatAsideRef.current.style.top = `${globalHeaderBottom}px`;
  }, [coachOpen, pageHeaderRef]);

  return (
    <div css={styles.panelGridWrapper}>
      {coachOpen && (
        <div
          ref={headerCoverRef}
          aria-hidden="true"
          css={styles.globalHeaderCover}
          data-testid="xdp-global-header-cover"
        />
      )}
      <div css={styles.panelGrid(chatOnLeft, coachOpen)}>
        {coachOpen && chatOnLeft && (
          <TwoPanelConversationalDiscoveryChatContainer chatOnLeft={chatOnLeft} ref={chatAsideRef} />
        )}
        <div css={styles.contentColumn(chatOnLeft, coachOpen)}>{children}</div>
        {coachOpen && chatOnRight && (
          <TwoPanelConversationalDiscoveryChatContainer chatOnLeft={chatOnLeft} ref={chatAsideRef} />
        )}
      </div>
    </div>
  );
};

export default TwoPanelConversationalDiscoveryLayout;
